import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Alert, Button, Dialog, Input, Tab } from '@enuygun/design-system'
import { CarRental, FlightRotated, Hotels } from '@enuygun/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'next-i18next'
import { useForm, Controller, Resolver } from 'react-hook-form'
import * as Yup from 'yup'

import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import { CheckFlightReservation, CheckHotelReservation } from 'services/queries'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import { isWingieCarRentalTabEnabled, isWingieHotelTabEnabled, WINGIE_SEARCH_FORM_TABS } from 'utils/searchForm'
import sendTrackerLog from 'utils/tracker'

import { buildCarRentalReservationUrl } from './buildCarRentalReservationUrl'
import { DialogInputContainer, DialogTabsWrapper } from './ManageMyBookingWingie.styled'

interface CheckReservationDialogProps {
  show: boolean
  setShow: (show: boolean) => void
}

interface ReservationFormValues {
  pnr: string
  lastname: string
}

const tabPanelIcons: Record<number, React.ReactNode> = {
  [WINGIE_SEARCH_FORM_TABS.flight]: <FlightRotated style={{ marginRight: '4px' }} width={18} height={18} />,
  [WINGIE_SEARCH_FORM_TABS.hotel]: <Hotels style={{ marginRight: '4px' }} width={18} height={18} />,
  [WINGIE_SEARCH_FORM_TABS.carRental]: <CarRental style={{ marginRight: '4px' }} width={18} height={18} />,
}

const tabPanelTitleKeys: Record<number, string> = {
  [WINGIE_SEARCH_FORM_TABS.flight]: 'flightSearchForm.tabTitle',
  [WINGIE_SEARCH_FORM_TABS.hotel]: 'hotelSearchForm.tabTitle',
  [WINGIE_SEARCH_FORM_TABS.carRental]: 'carRentalSearchForm.tabTitle',
}

const reservationSchema = Yup.object().shape({
  pnr: Yup.string().required('ManageMyBooking.nullPNR').min(5, 'ManageMyBooking.nullPNR'),
  lastname: Yup.string().required('ManageMyBooking.nullSurname'),
})

const getBookingType = (isHotelTab: boolean, isCarRentalTab: boolean) => {
  if (isHotelTab) return 'hotel'
  if (isCarRentalTab) return 'car-rental'
  return 'flight'
}

const CheckReservationDialog = ({ show, setShow }: CheckReservationDialogProps) => {
  const { t } = useTranslation('common')
  const { market, language, endpoint } = useAppState()
  const { isMobile, isTablet } = useDeviceType()
  const isWingie = market !== 'tr'
  const isHotelTabEnabled = isWingieHotelTabEnabled(language, market)
  const isCarRentalTabEnabled = isWingieCarRentalTabEnabled(language, market)
  const [activeTab, setActiveTab] = useState<number>(WINGIE_SEARCH_FORM_TABS.flight)
  const [notFound, setNotFound] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const isSubmitLockedRef = useRef(false)

  const isHotelTab = isHotelTabEnabled && activeTab === WINGIE_SEARCH_FORM_TABS.hotel
  const isCarRentalTab = isCarRentalTabEnabled && activeTab === WINGIE_SEARCH_FORM_TABS.carRental
  /** Tab bileşeni panelKey'i child sırası olarak yorumluyor; kapalı sekmelerde sıra kaymasın diye açık sekmeleri ayrı tutuyoruz. */
  const enabledTabs = useMemo(() => {
    const tabs: number[] = [WINGIE_SEARCH_FORM_TABS.flight]
    if (isHotelTabEnabled) tabs.push(WINGIE_SEARCH_FORM_TABS.hotel)
    if (isCarRentalTabEnabled) tabs.push(WINGIE_SEARCH_FORM_TABS.carRental)
    return tabs
  }, [isHotelTabEnabled, isCarRentalTabEnabled])

  const activeTabPosition = Math.max(enabledTabs.indexOf(activeTab), 0)
  const isReferenceInput = isHotelTab || isCarRentalTab

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReservationFormValues>({
    resolver: yupResolver(reservationSchema) as unknown as Resolver<ReservationFormValues>,
    defaultValues: { pnr: '', lastname: '' },
  })

  const isSubmitPending = isLoading || isSubmitting
  const pnrErrorKey = isReferenceInput ? 'ManageMyBooking.nullReference' : errors?.pnr?.message
  const pnrErrorMessage = errors?.pnr?.message && pnrErrorKey ? t(pnrErrorKey as any) : undefined

  useEffect(() => {
    if (!show) {
      setNotFound(null)
      setIsLoading(false)
      isSubmitLockedRef.current = false
      setActiveTab(WINGIE_SEARCH_FORM_TABS.flight)
      reset({ pnr: '', lastname: '' })
    }
  }, [show, reset])

  useEffect(() => {
    setNotFound(null)
  }, [activeTab])

  const trackSubmit = (data: ReservationFormValues) => {
    const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      type: 'click',
      message: 'manage_my_booking_dialog_submit',
      environment,
      requestId,
      pnrCode: data.pnr || null,
      lastName: data.lastname || null,
      bookingType: getBookingType(isHotelTab, isCarRentalTab),
      isMobile: !!(isMobile || isTablet),
      market,
    })
  }

  const handleFlightBooking = async (data: ReservationFormValues) => {
    const response = await CheckFlightReservation(data)

    if (response?.error) {
      setNotFound(response?.error_description)
      return false
    }

    if (response?.redirect_to) {
      globalThis.location.href = response.redirect_to
      return true
    }

    return false
  }

  const handleHotelBooking = async (data: ReservationFormValues) => {
    const response = await CheckHotelReservation({
      reference: data.pnr,
      lastName: data.lastname,
      market: market || 'us',
    })

    if (!response?.status) {
      setNotFound(t('ManageMyBooking.hotelNotFound'))
      return false
    }

    const params = new URLSearchParams({
      reference: data.pnr,
      lastName: data.lastname,
    })
    const base = endpoint || ''
    globalThis.location.href = `${base}/hotels/check-reservation/?${params.toString()}`
    return true
  }

  const handleCarRentalBooking = (data: ReservationFormValues) => {
    globalThis.location.href = buildCarRentalReservationUrl(endpoint, data.pnr, data.lastname)
    return true
  }

  const getCheckReservationHandler = () => {
    if (isHotelTab) return handleHotelBooking
    if (isCarRentalTab) return handleCarRentalBooking
    return handleFlightBooking
  }

  const handleBooking = async (data: ReservationFormValues) => {
    if (isSubmitLockedRef.current) return

    isSubmitLockedRef.current = true
    setIsLoading(true)
    setNotFound(null)
    trackSubmit(data)

    try {
      const checkReservation = getCheckReservationHandler()
      const redirected = await checkReservation(data)
      // Redirect sonrası loading kalsın; hata/no-result durumunda tekrar denenebilsin
      if (!redirected) {
        isSubmitLockedRef.current = false
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Hata oluştu:', error)
      if (isHotelTab) {
        setNotFound(t('ManageMyBooking.hotelNotFound'))
      }
      if (isCarRentalTab) {
        setNotFound(t('ManageMyBooking.carNotFound'))
      }
      isSubmitLockedRef.current = false
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      onOk={() => setShow(false)}
      onCancel={() => setShow(false)}
      title={t('ManageMyBooking.modal.title')}
      open={show}
      onClose={() => setShow(false)}
      footer={false}
      data-testid="wingie-homepage-modal-manageMyBooking"
      dataTestId="manageMyBooking"
    >
      {notFound && <Alert dataTestId="test" shape="outline" title={notFound} type="error" style={{ width: '100%' }} />}
      {enabledTabs.length > 1 && (
        <DialogTabsWrapper>
          <Tab
            shape="default"
            active={activeTabPosition}
            onClick={(_e, key) => setActiveTab(enabledTabs[Number(key) || 0] ?? WINGIE_SEARCH_FORM_TABS.flight)}
            dataTestId="manageMyBooking-tabs"
            isMobile={!!(isMobile || isTablet)}
          >
            {enabledTabs.map((tabIndex, position) => (
              <Tab.Panel key={tabIndex} panelKey={position} icon={tabPanelIcons[tabIndex]} unmount={false} justify="flex-center" title={t(tabPanelTitleKeys[tabIndex] as any)}>
                <span />
              </Tab.Panel>
            ))}
          </Tab>
        </DialogTabsWrapper>
      )}
      <form onSubmit={handleSubmit(handleBooking)}>
        <DialogInputContainer>
          <Controller
            name="pnr"
            control={control}
            render={({ field }) => (
              <Input
                status={errors.pnr ? 'error' : undefined}
                message={pnrErrorMessage}
                label={t(isReferenceInput ? 'ManageMyBooking.modal.inputReference' : 'ManageMyBooking.modal.inputPnr')}
                {...field}
                maxLength={32}
                data-testid="wingie-homepage-modal-pnrCodeInput"
              />
            )}
          />
          <Controller
            name="lastname"
            control={control}
            render={({ field }) => (
              <Input
                status={errors.lastname ? 'error' : undefined}
                shape="default"
                data-testid="wingie-homepage-modal-surNameInput"
                label={t('ManageMyBooking.modal.inputSurname')}
                message={errors?.lastname?.message && t(errors.lastname.message as any)}
                {...field}
              />
            )}
          />
          <Button loading={isSubmitPending} disabled={isSubmitPending} as="button" type="submit" data-testid="wingie-homepage-modal-searchButton">
            {t('ManageMyBooking.modal.searchBtn')}
          </Button>
        </DialogInputContainer>
      </form>
    </Dialog>
  )
}

export default CheckReservationDialog
